import { fetchPatientList } from '../../../services/apiService';
import type {
  ApiPartner,
  ApiPatient,
  FetchPatientsResponse,
  PatientRow,
} from './DocumentIntPatientList.types';

export const toISODate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

export const getDateRangeForLastMonth = () => {
  const today = new Date();
  const endDate = toISODate(today);
  const startDate = toISODate(
    new Date(new Date(today).setMonth(today.getMonth() - 1))
  );

  return { startDate, endDate };
};

export const formatUTCDate = (date: Date): string =>
  `${String(date.getUTCDate()).padStart(2, '0')}/${String(
    date.getUTCMonth() + 1
  ).padStart(2, '0')}/${date.getUTCFullYear()}`;

export const getFullName = (first?: string, last?: string): string =>
  `${first ?? ''} ${last ?? ''}`.trim() || '-';

export const getAvatar = (
  obj?: { photo?: string } | null
): string | undefined => obj?.photo;

export const normalizePatientRow = (patient: ApiPatient): PatientRow => {
  const partner: ApiPartner | null | undefined = patient?.partner;
  const createdDate = patient?.created_on ? new Date(patient.created_on) : null;

  return {
    id: patient.id,
    registrationDate: createdDate ? formatUTCDate(createdDate) : '-',
    name: getFullName(patient?.first_name, patient?.last_name),
    avatar: getAvatar(patient),
    partnerName: partner ? getFullName(partner?.first_name, partner?.last_name) : '-',
    partnerAvatar: partner ? getAvatar(partner) : '',
    mobile: patient?.mobile_number ?? '-',
    doctor: patient?.doctor_name ?? '-',
    reasonForVisit: patient?.reason_for_visit_label ?? '-',
    status: patient?.status
      ? patient.status.charAt(0).toUpperCase() + patient.status.slice(1).toLowerCase()
      : 'Active',
    raw: patient,
  };
};

export const buildPatientListQuery = (
  startDate: string,
  endDate: string,
  search?: string
): string => {
  const params = new URLSearchParams();

  params.append('patient_type', 'patient');
  params.append('created_on_after', startDate);
  params.append('created_on_before', endDate);
  params.append('limit', '100');
  params.append('offset', '0');

  if (search?.trim()) {
    params.append('search', search.trim());
  }

  return params.toString();
};

export const loadPatientRows = async (
  clinicId: string,
  startDate: string,
  endDate: string,
  search?: string
): Promise<PatientRow[]> => {
  const query = buildPatientListQuery(startDate, endDate, search);
  const response = (await fetchPatientList(query, clinicId)) as FetchPatientsResponse;

  return (response?.objects ?? []).map(normalizePatientRow);
};

export const filterPatients = (patients: PatientRow[], query: string): PatientRow[] => {
  const q = query.trim().toLowerCase();
  if (!q) return patients;

  return patients.filter((patient) => {
    return (
      patient.name.toLowerCase().includes(q) ||
      patient.partnerName.toLowerCase().includes(q) ||
      patient.mobile.toLowerCase().includes(q) ||
      patient.doctor.toLowerCase().includes(q) ||
      patient.reasonForVisit.toLowerCase().includes(q)
    );
  });
};

export const paginatePatients = (
  patients: PatientRow[],
  page: number,
  pageSize: number
): PatientRow[] => {
  const start = (page - 1) * pageSize;
  return patients.slice(start, start + pageSize);
};